/**
 * This PR creates client side code that performs the same functionality as qt-faststart from ffmpeg tools.
 * This code will convert a non-streamable MP4 into a streamable MP4 by moving the MOOV atom before the MDAT atom.
 * This code was based off the node qt-faststart https://github.com/kanongil/node-faststart and also the
 * ffmpeg qt-faststart https://ffmpeg.org/doxygen/0.6/qt-faststart_8c-source.html
 *
 * See https://www.adobe.com/devnet/video/articles/mp4_movie_atom.html for a quick overview on mp4 format and MOOV atom
 * Here's in depth information about each of the atoms in the Quicktime File Specification
 * https://developer.apple.com/library/archive/documentation/QuickTime/QTFF/QTFFChap2/qtff2.html
 */
import { MAX_FILE_SIZE_PER_REQUEST } from './network';
import { warn, debug } from './log';

const EOF = 'EOF';
const MAX_UINT32 = Math.pow(2, 32) - 1;
// Due to platform limitations 64-bit values can't have more than 52 bits of data
const MAX_UINT64 = Math.pow(2, 52) - 1;
const UPDATE_ATOMS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl']);
const QT_ATOMS = {
  free: 'free',
  junk: 'junk',
  mdat: 'mdat',
  moov: 'moov',
  pnot: 'pnot',
  skip: 'skip',
  wide: 'wide',
  pict: 'pict',
  ftyp: 'ftyp',
  uuid: 'uuid',
  cmov: 'cmov',
  stco: 'stco',
  co64: 'co64',
};

export const ALREADY_FAST_START_ERR = 'File is already fast start';

const concatArrBuff = (a1, a2) => {
  const concatBlob = new Blob([a1, a2]);
  return concatBlob.arrayBuffer();
};

const dataViewSetAscii = (dv, byteOffset, str) => {
  const arr = [];
  let char;
  for (let i = 0; i < str.length; i++) {
    char = str.charCodeAt(i);
    if (char > 255) {
      char = '?'.charCodeAt(0);
    }
    arr.push(char);
  }

  for (let i = 0; i < arr.length && byteOffset + i < dv.byteLength; i++) {
    dv.setUint8(byteOffset + i, arr[i]);
  }
};

function getUint64(dataView, byteOffset, littleEndian) {
  // split 64-bit number into two 32-bit (4-byte) parts
  const left = dataView.getUint32(byteOffset, littleEndian);
  const right = dataView.getUint32(byteOffset + 4, littleEndian);

  // combine the two 32-bit values
  const combined = littleEndian
    ? left + 2 ** 32 * right
    : 2 ** 32 * left + right;

  if (!Number.isSafeInteger(combined)) {
    warn(combined, 'exceeds MAX_SAFE_INTEGER. Precision may be lost');
  }

  return combined;
}

const readArrBuffFromFile = (file, start, end) => {
  const reader = new FileReader();
  const blob = file.slice(start, end);
  debug('readArrBuffFromFile', start, end);

  reader.readAsArrayBuffer(blob);

  return new Promise((res, rej) => {
    reader.onloadend = (event) => {
      if (event.target.readyState === FileReader.DONE) {
        // Data has been read, invoke the complete method!
        res(event.target.result);
      }
    };

    reader.onerror = (event) => {
      rej(event.target.error);
    };
  });
};

const readAtom = async (file, offset) => {
  let data = await readArrBuffFromFile(file, offset, offset + 8);
  if (data.byteLength < 8) return EOF;

  const atom = { size: 0, type: '', skip: 8, offset };

  let dataView = new DataView(data);
  atom.size = dataView.getUint32(0, false);
  atom.type = String.fromCharCode.apply(String, new Uint8Array(data).slice(4));

  if (atom.size === 1) {
    data = await readArrBuffFromFile(file, offset + 8, offset + 16);
    dataView = new DataView(data);
    atom.size = getUint64(dataView, 0, false);
    if (atom.size > MAX_UINT64) {
      throw Error('size too large to calculate');
    }
    atom.skip = 16;
  }

  debug('Read an atom', atom);

  if (atom.type === QT_ATOMS.cmov) {
    throw Error('this utility does not support compressed moov atoms yet');
  }

  return atom;
};

// TODO: limit index size?
const createAtomReader = async (file) => {
  const atoms = [];
  let moovIndex = null;
  let mdatIndex = null;
  let done = false;
  let offset = 0;

  while (!done) {
    const atom = await readAtom(file, offset);
    if (atom === EOF) {
      break;
    }

    if (!QT_ATOMS[atom.type]) {
      throw Error(
        'encountered non-QT top-level atom (is this a Quicktime file?)',
      );
    }

    if (atom.offset === 0 && atom.type !== QT_ATOMS.ftyp) {
      throw Error('Unknown/Unsupported file type.');
    }

    if (atom.type === QT_ATOMS.moov) {
      if (moovIndex) {
        throw Error(`Multiple "${QT_ATOMS.moov}" fragments.`);
      }
      moovIndex = atoms.length;
    }

    if (atom.type === QT_ATOMS.mdat) {
      if (mdatIndex) {
        throw Error(`Multiple "${QT_ATOMS.mdat}" fragments.`);
      }
      mdatIndex = atoms.length;
    }

    // mdat atoms are allowed to have 0 size if they are at the end
    if (atom.size === 0 && atom.type === QT_ATOMS.mdat) {
      // calculate the actual size
      atom.size = file.size - atom.offset;
      done = true;
    } else if (atom.size < 8) {
      // ensure we don't end in an infinite loop
      throw Error('Invalid atom size (less than 8 bytes).');
    }

    atoms.push(atom);
    offset = atom.offset + atom.size;
  }

  if (atoms.length === 0) {
    throw Error('Failing to parse atoms.');
  }

  if (!moovIndex || !mdatIndex) {
    throw Error('Missing moov/mdat atom.');
  }

  return { atoms, mdatIndex, moovIndex };
};

/**
 * Takes a buffer containing all data except mdat (video data) and the original video file.
 * Then it adds appends mdat from video file. This creates a streamable video.
 * It sends the streamable video data to onChunk handler in the specified chunk
 * sizes. The initial chunk size is the size of the first chunk.
 * @param file
 * @param atomsExcludingMdatBuffer
 * @param mdatStartOffset
 * @param mdatEndOffset
 * @param initialChunkSize
 * @param chunkSize
 * @param onChunk
 * @returns {Promise<void>}
 */
const sendConvertedVideo = async (
  file,
  atomsExcludingMdatBuffer,
  mdatStartOffset,
  mdatEndOffset,
  initialChunkSize,
  chunkSize,
  onChunk,
) => {
  let firstChunk;
  let mdatOffset = mdatStartOffset;
  let leftover = new ArrayBuffer(0);
  if (atomsExcludingMdatBuffer.byteLength === initialChunkSize) {
    firstChunk = atomsExcludingMdatBuffer;
  } else if (atomsExcludingMdatBuffer.byteLength < initialChunkSize) {
    const newMdatOffset = Math.min(
      mdatOffset + (initialChunkSize - atomsExcludingMdatBuffer.byteLength),
      mdatEndOffset,
    );
    firstChunk = await concatArrBuff(
      atomsExcludingMdatBuffer,
      await readArrBuffFromFile(file, mdatOffset, newMdatOffset),
    );
    mdatOffset = newMdatOffset;
  } else {
    firstChunk = atomsExcludingMdatBuffer.slice(0, initialChunkSize);
    leftover = atomsExcludingMdatBuffer.slice(initialChunkSize);
  }

  await onChunk(firstChunk);
  while (leftover.byteLength) {
    if (leftover.byteLength < chunkSize) {
      const newMdatOffset = Math.min(
        mdatOffset + (chunkSize - leftover.byteLength),
        mdatEndOffset,
      );
      const buff = await concatArrBuff(
        leftover,
        await readArrBuffFromFile(file, mdatOffset, newMdatOffset),
      );
      await onChunk(buff);
      mdatOffset = newMdatOffset;
      break;
    }

    await onChunk(leftover.slice(0, chunkSize));
    leftover = leftover.slice(chunkSize);
  }

  while (mdatOffset < mdatEndOffset) {
    const newMdatOffset = Math.min(mdatOffset + chunkSize, mdatEndOffset);
    const buff = await readArrBuffFromFile(file, mdatOffset, newMdatOffset);
    await onChunk(buff);
    mdatOffset = newMdatOffset;
  }
};

const passThroughAtom = async (file, atom, offset, onDataBuffer) => {
  const buf = await readArrBuffFromFile(
    file,
    atom.offset,
    atom.offset + atom.size,
  );

  if (!offset) {
    return onDataBuffer(buf);
  }

  const dv = new DataView(buf);
  if (atom.type === QT_ATOMS.stco) {
    const count = dv.getUint32(12, false);

    for (let i = 0, pos = 16; i < count; i++, pos += 4) {
      const newOffset = dv.getUint32(pos, false) + offset;
      if (newOffset > MAX_UINT32) {
        throw Error(
          `Bad input file: "${QT_ATOMS.stco}" atom found, but "${QT_ATOMS.co64}" is required for fast start.`,
        );
      }
      dv.setUint32(pos, newOffset, false);
    }
  } else if (atom.type === QT_ATOMS.co64) {
    const count = dv.getUint32(12, false);
    for (let i = 0, pos = 16; i < count; i++, pos += 8) {
      // 64-bit 2's-complement math (with assumptions for speed)
      let hi = dv.getUint32(pos, false);
      let lo = dv.getUint32(pos + 4, false) + offset;
      while (lo > MAX_UINT32) {
        lo -= MAX_UINT32 + 1;
        hi += 1;
      }
      dv.setUint32(hi, pos, false);
      dv.setUint32(pos + 4, lo, false);
    }
  }

  return onDataBuffer(buf);
};

const processAtom = async (file, atom, offset, onDataBuffer) => {
  debug('processAtom called', atom, offset);
  const end = atom.offset + atom.size;

  if (!UPDATE_ATOMS.has(atom.type)) {
    await passThroughAtom(file, atom, offset, onDataBuffer);
    return;
  }

  // emit container atom header
  const buf = new ArrayBuffer(atom.skip);
  const dv = new DataView(buf);
  if (atom.skip === 16) {
    dv.setUint32(0, 1, false);
    dv.setUint32(8, atom.size >> 32, false);
    dv.setUint32(12, atom.size, false);
  } else {
    dv.setUint32(0, atom.size, false);
  }
  dataViewSetAscii(dv, 4, atom.type);
  await onDataBuffer(buf);

  // read and parse sub-atoms
  let nextAtom = await readAtom(file, atom.offset + atom.skip);
  while (true) {
    const atomEnd = nextAtom.offset + nextAtom.size;
    if (atomEnd > end) {
      throw Error('Invalid atom structure.');
    }

    // ensure we don't end in an infinite loop
    if (nextAtom.size < 8) {
      throw Error('Invalid atom size (less than 8 bytes).');
    }

    await processAtom(file, nextAtom, offset, onDataBuffer);

    if (atomEnd === end) {
      break;
    }

    nextAtom = await readAtom(file, atomEnd);
  }
};

/**
 * Retrieve all data from the video excluding the MDAT data and anything before MOOV atom
 * @param file
 * @param atomsExcludingMdat
 * @param offset
 * @returns {Promise<void>}
 */
const getVideoFileBufferExcludingMdat = async (
  file,
  atomsExcludingMdat,
  offset,
) => {
  let result = new ArrayBuffer(0);
  const appendToBuffer = async (newArrBuffData) => {
    result = await concatArrBuff(result, newArrBuffData);
  };

  for (let i = 0; i < atomsExcludingMdat.length; i++) {
    const baseAtom = atomsExcludingMdat[i];
    await processAtom(file, baseAtom, offset, appendToBuffer);
  }

  return result;
};

const convertToStreamable = async (
  file,
  chunkSize,
  initialChunkSize,
  onChunk,
) => {
  const { atoms, moovIndex, mdatIndex } = await createAtomReader(file);

  const isFileFastStart = atoms[moovIndex].offset < atoms[mdatIndex].offset;
  if (isFileFastStart) {
    throw Error(ALREADY_FAST_START_ERR);
  }

  const atomsExcludingMdat = [atoms[0]].concat(atoms.slice(moovIndex)); // ftyp + moov + any extra
  const mdatAndOtherAtomsBetweenFtypAndMoov = atoms.slice(1, moovIndex); // mdat + remaining

  const first = atomsExcludingMdat[1];
  const last = atomsExcludingMdat.slice(-1)[0];
  const offset = last.offset + last.size - first.offset;

  const mdatStartOffset = mdatAndOtherAtomsBetweenFtypAndMoov[0].offset;
  const mdatEndOffset =
    mdatAndOtherAtomsBetweenFtypAndMoov.slice(-1)[0].offset +
    mdatAndOtherAtomsBetweenFtypAndMoov.slice(-1)[0].size;
  const videoFileBufferExcludingMdat = await getVideoFileBufferExcludingMdat(
    file,
    atomsExcludingMdat,
    offset,
  );
  await sendConvertedVideo(
    file,
    videoFileBufferExcludingMdat,
    mdatStartOffset,
    mdatEndOffset,
    initialChunkSize,
    chunkSize,
    onChunk,
  );
};

export const getFileParts = async (
  file,
  maxFileSize = MAX_FILE_SIZE_PER_REQUEST,
  initialFileSize = MAX_FILE_SIZE_PER_REQUEST,
) => {
  const fileParts = [];
  await convertToStreamable(
    file,
    maxFileSize,
    initialFileSize,
    (chunkBuffer) => {
      fileParts.push(new Blob([chunkBuffer]));
    },
  );
  debug('video streamable conversion result', {
    fileParts,
    fileSize: file.size,
  });
  return { fileParts, fileSize: file.size };
};
